// Complete Catalog Service for Firebase
// Handles catalog items, creators, and collections

import { db } from '../../../firebase/config';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  limit,
  deleteDoc,
  updateDoc,
  orderBy 
} from 'firebase/firestore';
import { CatalogItem, CreatorAuthority, CollectionRecord, CreatorRole } from '../../../catalog/types';

class CatalogService {
  private catalogItemsCollection = 'catalog-items';
  private creatorsCollection = 'creator-authorities';
  private collectionsCollection = 'collections';

  // ═══════════════════════════════════════════════════════════
  // CATALOG ITEMS
  // ═══════════════════════════════════════════════════════════

  async saveCatalogItem(item: CatalogItem): Promise<string> {
    try {
      const itemId = item.id || `item-${Date.now()}`;
      const itemRef = doc(db, this.catalogItemsCollection, itemId);
      
      const dataToSave = {
        ...item,
        id: itemId,
        lastModified: new Date().toISOString()
      };
      
      console.log('💾 Saving catalog item:', itemId);
      await setDoc(itemRef, dataToSave);
      console.log('✅ Catalog item saved successfully');
      
      return itemId;
    } catch (error) {
      console.error('❌ Catalog item save error:', error);
      throw error;
    }
  }

  async getCatalogItem(itemId: string): Promise<CatalogItem | null> {
    try {
      const itemRef = doc(db, this.catalogItemsCollection, itemId);
      const docSnap = await getDoc(itemRef);
      
      if (docSnap.exists()) {
        return docSnap.data() as CatalogItem;
      }
      return null;
    } catch (error) {
      console.error('❌ Get catalog item error:', error);
      return null;
    }
  }

  async getAllCatalogItems(maxResults: number = 100): Promise<CatalogItem[]> {
    try {
      const q = query(
        collection(db, this.catalogItemsCollection),
        orderBy('lastModified', 'desc'),
        limit(maxResults)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => doc.data() as CatalogItem);
    } catch (error) {
      console.error('❌ Get all items error:', error);
      return [];
    }
  }

  async searchByTitle(titleQuery: string): Promise<CatalogItem[]> {
    try {
      const q = query(
        collection(db, this.catalogItemsCollection),
        where('titleProper', '>=', titleQuery),
        where('titleProper', '<=', titleQuery + '\uf8ff'),
        limit(50)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => doc.data() as CatalogItem);
    } catch (error) {
      console.error('❌ Search error:', error);
      return [];
    }
  }

  async deleteCatalogItem(itemId: string): Promise<void> {
    try {
      const itemRef = doc(db, this.catalogItemsCollection, itemId);
      await deleteDoc(itemRef);
      console.log('✅ Deleted catalog item:', itemId);
    } catch (error) {
      console.error('❌ Delete error:', error);
      throw error;
    }
  }

  // ═══════════════════════════════════════════════════════════
  // CREATORS (AUTHORITY RECORDS)
  // ═══════════════════════════════════════════════════════════

  /**
   * Save a creator authority record to Firebase
   * @param creator - CreatorAuthority object
   * @returns Promise<string> - The creator ID
   */
  async saveCreator(creator: CreatorAuthority): Promise<string> {
    try {
      const creatorId = creator.id || `creator-${Date.now()}`;
      const creatorRef = doc(db, this.creatorsCollection, creatorId);
      
      const dataToSave = {
        ...creator,
        id: creatorId,
        lastModified: new Date().toISOString(),
        createdDate: creator.createdDate || new Date().toISOString()
      };
      
      console.log('💾 Saving creator to Firebase:', creatorId);
      console.log('📊 Creator data:', {
        id: dataToSave.id,
        preferredName: dataToSave.preferredName,
        primaryRole: dataToSave.primaryRole
      });
      
      await setDoc(creatorRef, dataToSave);
      
      console.log('✅ Creator saved successfully to Firebase!');
      console.log(`✅ Collection: ${this.creatorsCollection}`);
      console.log(`✅ Document ID: ${creatorId}`);
      
      return creatorId;
      
    } catch (error) {
      console.error('❌ Creator save error:', error);
      console.error('Error details:', error);
      throw error;
    }
  }

  async getCreator(creatorId: string): Promise<CreatorAuthority | null> {
    try {
      const creatorRef = doc(db, this.creatorsCollection, creatorId);
      const docSnap = await getDoc(creatorRef);
      
      if (docSnap.exists()) {
        return docSnap.data() as CreatorAuthority;
      }
      return null;
    } catch (error) {
      console.error('❌ Get creator error:', error);
      return null;
    }
  }

  async getAllCreators(): Promise<CreatorAuthority[]> {
    try {
      const q = query(
        collection(db, this.creatorsCollection),
        orderBy('preferredName', 'asc')
      );
      
      const snapshot = await getDocs(q);
      const creators = snapshot.docs.map(doc => doc.data() as CreatorAuthority);
      
      console.log(`✅ Retrieved ${creators.length} creators from Firebase`);
      return creators;
    } catch (error) {
      console.error('❌ Get all creators error:', error);
      return [];
    }
  }

  async searchCreatorsByName(nameQuery: string): Promise<CreatorAuthority[]> {
    try {
      const q = query(
        collection(db, this.creatorsCollection),
        where('preferredName', '>=', nameQuery),
        where('preferredName', '<=', nameQuery + '\uf8ff'),
        limit(50)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => doc.data() as CreatorAuthority);
    } catch (error) {
      console.error('❌ Creator search error:', error);
      return [];
    }
  }

  async getCreatorsByRole(role: string): Promise<CreatorAuthority[]> {
    try {
      const q = query(
        collection(db, this.creatorsCollection),
        where('primaryRole', '==', role)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => doc.data() as CreatorAuthority);
    } catch (error) {
      console.error('❌ Get creators by role error:', error);
      return [];
    }
  }

  async updateCreator(creatorId: string, updates: Partial<CreatorAuthority>): Promise<void> {
    try {
      const creatorRef = doc(db, this.creatorsCollection, creatorId);
      await updateDoc(creatorRef, {
        ...updates,
        lastModified: new Date().toISOString()
      });
      console.log('✅ Updated creator:', creatorId);
    } catch (error) {
      console.error('❌ Update creator error:', error);
      throw error;
    }
  }

  async deleteCreator(creatorId: string): Promise<void> {
    try {
      const creatorRef = doc(db, this.creatorsCollection, creatorId);
      await deleteDoc(creatorRef);
      console.log('✅ Deleted creator:', creatorId);
    } catch (error) {
      console.error('❌ Delete creator error:', error);
      throw error;
    }
  }

  // ═══════════════════════════════════════════════════════════
  // HELPER METHODS: Link creators to catalog items
  // ═══════════════════════════════════════════════════════════

  /**
   * Link a creator to a catalog item
   * @param catalogItemId - ID of the catalog item
   * @param creatorId - ID of the creator
   * @param role - Creator's role in this work
   * @param attribution - How creator is credited (optional)
   */
  async linkCreatorToCatalogItem(
    catalogItemId: string, 
    creatorId: string, 
    role: CreatorRole,
    attribution?: string
  ): Promise<void> {
    try {
      // Get the catalog item
      const item = await this.getCatalogItem(catalogItemId);
      if (!item) {
        throw new Error(`Catalog item ${catalogItemId} not found`);
      }

      // Check if creator exists
      const creator = await this.getCreator(creatorId);
      if (!creator) {
        throw new Error(`Creator ${creatorId} not found`);
      }

      // Add creator reference to catalog item
      const updatedCreators = item.creators || [];
      
      // Check if already linked
      const existingIndex = updatedCreators.findIndex(c => c.creatorId === creatorId);
      
      if (existingIndex >= 0) {
        // Update existing reference
        updatedCreators[existingIndex] = {
          creatorId,
          role,
          attribution: attribution || creator.preferredName
        };
      } else {
        // Add new reference
        updatedCreators.push({
          creatorId,
          role,
          attribution: attribution || creator.preferredName
        });
      }

      // Update the catalog item
      const itemRef = doc(db, this.catalogItemsCollection, catalogItemId);
      await updateDoc(itemRef, {
        creators: updatedCreators,
        lastModified: new Date().toISOString()
      });

      console.log(`✅ Linked creator ${creatorId} to catalog item ${catalogItemId}`);
    } catch (error) {
      console.error('❌ Link creator error:', error);
      throw error;
    }
  }

  /**
   * Get all catalog items by a specific creator
   * @param creatorId - ID of the creator
   * @returns Array of catalog items
   */
  async getCatalogItemsByCreator(creatorId: string): Promise<CatalogItem[]> {
    try {
      const allItems = await this.getAllCatalogItems(1000);
      return allItems.filter(item => 
        item.creators?.some(c => c.creatorId === creatorId)
      );
    } catch (error) {
      console.error('❌ Get items by creator error:', error);
      return [];
    }
  }

  // ═══════════════════════════════════════════════════════════
  // COLLECTIONS
  // ═══════════════════════════════════════════════════════════

  async saveCollection(collectionRecord: CollectionRecord): Promise<string> {
    try {
      const collectionId = collectionRecord.id || `collection-${Date.now()}`;
      const collectionRef = doc(db, this.collectionsCollection, collectionId);
      
      const dataToSave = {
        ...collectionRecord,
        id: collectionId,
        lastModified: new Date().toISOString()
      };
      
      await setDoc(collectionRef, dataToSave);
      console.log('✅ Saved collection:', collectionId);
      return collectionId;
    } catch (error) {
      console.error('❌ Collection save error:', error);
      throw error;
    }
  }

  async getCollection(collectionId: string): Promise<CollectionRecord | null> {
    try {
      const collectionRef = doc(db, this.collectionsCollection, collectionId);
      const docSnap = await getDoc(collectionRef);
      
      if (docSnap.exists()) {
        return docSnap.data() as CollectionRecord;
      }
      return null;
    } catch (error) {
      console.error('❌ Get collection error:', error);
      return null;
    }
  }

  async getAllCollections(): Promise<CollectionRecord[]> {
    try {
      const snapshot = await getDocs(collection(db, this.collectionsCollection));
      return snapshot.docs.map(doc => doc.data() as CollectionRecord);
    } catch (error) {
      console.error('❌ Get collections error:', error);
      return [];
    }
  }

  async deleteCollection(collectionId: string): Promise<void> {
    try {
      const collectionRef = doc(db, this.collectionsCollection, collectionId);
      await deleteDoc(collectionRef);
      console.log('✅ Deleted collection:', collectionId);
    } catch (error) {
      console.error('❌ Delete collection error:', error);
      throw error;
    }
  }
}

export const catalogService = new CatalogService();
